import { useMemo } from 'react';
import { useDataFetch } from '../../../Hooks/Use-data-fetch';

/**
 * Crecimiento de ventas, mes a mes o día a día.
 *
 * La configuración de apexcharts se arma aquí porque la librería NO hereda
 * la paleta de la hoja de estilos: sus colores por defecto son los suyos
 * (README, Advertencia 5). Todo lo que se ve —barras, rejilla, ejes,
 * tooltip— va explícito con los tokens del diseño índigo.
 */

// Tokens del handoff. Duplicados aquí a propósito: apexcharts recibe
// strings, no variables SCSS, y no hay puente entre los dos.
const MUTED = '#8A91AE';
const GRID = '#EDEFF6';
const BAR = '#6E63FF';
const BAR_LAST = '#4B36F0'; // el mes en curso, en índigo pleno
const FONT = "'Plus Jakarta Sans', system-ui, sans-serif";

const useOrderOverview = (selectedCountry, groupBy = 'month', months) => {
  const { getData } = useDataFetch();

  const { data, isLoading, error } = getData(
    ['growth_metrics', selectedCountry, groupBy],
    '/charts/growth-metrics',
    { enabled: !!selectedCountry },
    {
      countryId: selectedCountry,
      groupBy
    }
  );

  const processedData = useMemo(() => {
    const totalSales = data?.data?.totalSalesCount?.total ?? 0;

    const groups = Array.isArray(data?.data?.totalSalesCount?.groups)
      ? data.data.totalSalesCount.groups.filter((item) => item && item.label)
      : [];

    // El recorte es del lado del cliente: el endpoint devuelve el histórico
    // completo y en móvil doce barras a 390px quedan ilegibles.
    const monthlySales = months ? groups.slice(-months) : groups;

    return {
      totalSales,
      monthlySales,
      chartConfig: generateChartConfig(monthlySales)
    };
  }, [data, months]);

  return {
    ...processedData,
    isLoading,
    error
  };
};

const generateChartConfig = (monthlySales = []) => {
  const categories = monthlySales.map((item) => item.label);
  const salesData = monthlySales.map((item) => Number(item.total) || 0);
  const last = salesData.length - 1;

  return {
    series: [
      {
        name: 'Ventas netas',
        data: salesData
      }
    ],

    options: {
      chart: {
        id: 'sales-growth',
        type: 'bar',
        // Apilado con una sola serie es un no-op, pero deja el gráfico listo
        // para la segunda barra (comisiones) que el diseño pide y que hoy
        // ningún endpoint devuelve mes a mes.
        stacked: true,
        toolbar: { show: false },
        zoom: { enabled: false },
        fontFamily: FONT
      },

      colors: [BAR],

      plotOptions: {
        bar: {
          columnWidth: '52%',
          borderRadius: 4,
          borderRadiusApplication: 'end'
        }
      },

      states: {
        hover: { filter: { type: 'darken', value: 0.92 } },
        active: { filter: { type: 'none' } }
      },

      // El mes en curso se distingue del histórico, como en el diseño.
      fill: {
        colors: [({ dataPointIndex }) => (dataPointIndex === last ? BAR_LAST : BAR)]
      },

      dataLabels: { enabled: false },

      grid: {
        borderColor: GRID,
        strokeDashArray: 4,
        padding: { left: 5, right: 10, top: 10 }
      },

      xaxis: {
        categories,
        axisBorder: { show: false },
        axisTicks: { show: false },
        labels: {
          style: { colors: MUTED, fontSize: '10.5px', fontWeight: 600 }
        }
      },

      yaxis: {
        min: 0,
        tickAmount: 4,
        labels: {
          formatter: (value) => Number(value).toLocaleString('es-CO'),
          style: { colors: MUTED, fontSize: '11px' }
        }
      },

      tooltip: {
        theme: 'light',
        style: { fontFamily: FONT },
        y: {
          formatter: (value) => `${Number(value).toLocaleString('es-CO')} ventas`
        }
      },

      legend: { show: false },

      noData: {
        text: 'Sin datos para el período',
        style: { color: MUTED, fontFamily: FONT, fontSize: '13px' }
      },

      responsive: [
        { breakpoint: 1200, options: { chart: { height: 240 } } },
        { breakpoint: 768, options: { chart: { height: 200 } } }
      ]
    }
  };
};

export default useOrderOverview;
